import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { C, FONT, glow, hexToRgba, prefersReducedMotion } from '../theme/tokens';

export interface EffectStat {
  label: string;
  percent: number;
  /** Whether this line is bad for you. Deliberately NOT derived from the
   * sign: Wolf-Rayet's Signature Radius is -22% and a *buff* (smaller sig is
   * harder to hit), while Pulsar's is +30% and a *penalty*. Colouring by sign
   * would tell a pilot the opposite of the truth on both. */
  isPenalty: boolean;
}

/**
 * A wormhole system's effect, as a readable stat table.
 *
 * The version this replaces set the stat *labels* in Share Tech Mono at
 * dimmed alpha. Measured, that was 10.8:1 contrast — well past the AA floor —
 * so the reported "bad label text" was never a contrast problem. It was a
 * voice problem: "Armor HP" is an English label, not a measurement, and Share
 * Tech Mono is a thin display face that punishes running words at 13px. Both
 * DESIGN.md's Mono-Signal Rule ("mono for real data values only") and the
 * craft floor's ban on mono-as-technical-costume already said so.
 *
 * So the label wears the label voice (Rajdhani, drawn for small UI text) at
 * full text colour, and the mono face is kept for the thing that actually is
 * a measurement — the percentage.
 *
 * The magnitude bar runs one direction only, coloured by buff/penalty. A
 * left/right diverging bar would encode the sign, which is precisely the
 * wrong axis given the sign and the benefit are independent here.
 */
const EffectStats: React.FC<{ stats: EffectStat[] }> = ({ stats }) => {
  const reduced = prefersReducedMotion();
  // Bars are relative to the strongest line in THIS effect, so the shape of
  // the trade-off reads the same in a C1 as in a C6.
  const peak = Math.max(1, ...stats.map((s) => Math.abs(s.percent)));

  return (
    <Stack spacing={0.25}>
      {stats.map((s, i) => {
        const accent = s.isPenalty ? C.red : C.cyan;
        return (
          <Box
            key={s.label}
            sx={{
              px: 1,
              py: 0.6,
              borderRadius: '6px',
              transition: 'background-color 0.2s ease',
              '&:hover': { backgroundColor: hexToRgba(accent, 0.08) },
              animation: reduced ? 'none' : `statIn 0.4s cubic-bezier(0.16,1,0.3,1) ${i * 60}ms both`,
              '@keyframes statIn': {
                '0%': { opacity: 0, transform: 'translateY(4px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography
                sx={{
                  fontFamily: FONT.sub,
                  fontWeight: 600,
                  fontSize: '0.92rem',
                  letterSpacing: '0.01em',
                  color: C.text,
                  lineHeight: 1.3,
                  // Labels now name every weapon system an effect reaches
                  // ("Turret, Missile, Drone & Fighter Damage"), so they are
                  // long enough to need a flex item that may actually shrink.
                  // Without minWidth:0 the default `auto` floors this at the
                  // text's own width and pushes the percentage off the panel.
                  minWidth: 0,
                }}
              >
                {s.label}
              </Typography>

              {/* A dotted leader ties the label to its number across the gap —
                  the classic table device, and what stops the eye losing the
                  row on a wide panel. */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 8,
                  alignSelf: 'center',
                  borderBottom: `1px dotted ${hexToRgba(C.text, 0.22)}`,
                }}
              />

              <Typography
                sx={{
                  fontFamily: FONT.mono,
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: accent,
                  textShadow: glow(accent, 10, 0.35),
                  flexShrink: 0,
                  lineHeight: 1.2,
                }}
              >
                {s.percent > 0 ? '+' : ''}
                {s.percent}%
              </Typography>
            </Stack>

            <Box
              sx={{
                mt: 0.5,
                height: 3,
                borderRadius: 999,
                backgroundColor: hexToRgba(accent, 0.14),
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${Math.max(4, (Math.abs(s.percent) / peak) * 100)}%`,
                  borderRadius: 999,
                  backgroundColor: accent,
                  transformOrigin: 'left',
                  animation: reduced ? 'none' : `statBar 0.55s cubic-bezier(0.16,1,0.3,1) ${i * 60 + 120}ms both`,
                  '@keyframes statBar': {
                    '0%': { transform: 'scaleX(0)' },
                    '100%': { transform: 'scaleX(1)' },
                  },
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default EffectStats;
